export interface BoqLineImportPreviewRow {
  key: number;
  category: string;
  itemCode?: string;
  itemName: string;
  description?: string;
  revision?: string;
  quantity: number;
  uom: string;
  unitCost: number;
  lineTotal: number;
  status: 'valid' | 'error';
  errorMessage?: string;
}

export const BOQ_LINE_IMPORT_TEMPLATE_CSV = `Category,Item Code,Item Name,Description,Revision,Quantity,UOM,Unit Cost
Manufacturing,BOM-0001,Sample Item,"Example line description",,8,NUM,9300
`;

function cellStr(row: unknown[], idx: number): string {
  if (idx < 0) return '';
  const v = row[idx];
  return v != null ? String(v).trim() : '';
}

export function parseImportRow(row: unknown[], headerRow: string[]): BoqLineImportPreviewRow | null {
  const norm = (h: string) => h.trim().toLowerCase().replace(/\s+/g, ' ');
  const idx = (names: string[]) => {
    for (const n of names) {
      const i = headerRow.findIndex((h) => norm(String(h)) === norm(n));
      if (i >= 0) return i;
    }
    return -1;
  };

  const hasHeaders = norm(String(headerRow[0] ?? '')) === 'category';
  const qtyAt = (i: number) => parseFloat(cellStr(row, i));

  let category: string;
  let itemCode: string | undefined;
  let itemName: string;
  let description: string | undefined;
  let revision: string | undefined;
  let quantity: number;
  let uom: string;
  let unitCost: number;

  if (hasHeaders) {
    category = cellStr(row, idx(['category']));
    itemCode = cellStr(row, idx(['item code', 'itemcode'])) || undefined;
    itemName = cellStr(row, idx(['item name', 'itemname']));
    description = cellStr(row, idx(['description'])) || undefined;
    revision = cellStr(row, idx(['revision'])) || undefined;
    quantity = qtyAt(idx(['quantity']));
    uom = cellStr(row, idx(['uom']));
    unitCost = qtyAt(idx(['unit cost', 'unitcost']));
  } else {
    const revCol = cellStr(row, 4);
    const qtyLegacy = qtyAt(4);
    const qtyWithRev = qtyAt(5);
    const useRevisionColumn =
      revCol.length > 0 &&
      revCol.length <= 20 &&
      (!Number.isFinite(qtyLegacy) || qtyLegacy <= 0) &&
      Number.isFinite(qtyWithRev) &&
      qtyWithRev > 0;

    category = cellStr(row, 0);
    itemCode = cellStr(row, 1) || undefined;
    itemName = cellStr(row, 2);
    description = cellStr(row, 3) || undefined;
    if (useRevisionColumn) {
      revision = revCol || undefined;
      quantity = qtyWithRev;
      uom = cellStr(row, 6);
      unitCost = qtyAt(7);
    } else {
      revision = undefined;
      quantity = qtyLegacy;
      uom = cellStr(row, 5);
      unitCost = qtyAt(6);
    }
  }

  if (!category && !itemName) return null;

  let status: 'valid' | 'error' = 'valid';
  let errorMessage = '';
  if (!itemName) {
    status = 'error';
    errorMessage = 'Item name is required';
  } else if (!category) {
    status = 'error';
    errorMessage = 'Category is required';
  } else if (!Number.isFinite(quantity) || quantity <= 0) {
    status = 'error';
    errorMessage = 'Quantity must be greater than 0';
  } else if (!uom) {
    status = 'error';
    errorMessage = 'UOM is required';
  } else if (!Number.isFinite(unitCost) || unitCost < 0) {
    status = 'error';
    errorMessage = 'Unit cost cannot be negative';
  }

  return {
    key: 0,
    category,
    itemCode,
    itemName,
    description,
    revision,
    quantity: Number.isFinite(quantity) ? quantity : 0,
    uom,
    unitCost: Number.isFinite(unitCost) ? unitCost : 0,
    lineTotal: (Number.isFinite(quantity) ? quantity : 0) * (Number.isFinite(unitCost) ? unitCost : 0),
    status,
    errorMessage,
  };
}

export async function parseBoqImportFile(file: File): Promise<BoqLineImportPreviewRow[]> {
  const XLSX = await import('xlsx');
  const data = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (result instanceof ArrayBuffer) resolve(result);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });

  const workbook = XLSX.read(data, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return [];
  const worksheet = workbook.Sheets[firstSheetName];
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
  if (jsonData.length < 2) return [];

  const headerRow = (jsonData[0] ?? []).map((h) => String(h ?? ''));
  const parsedData: BoqLineImportPreviewRow[] = [];
  let rowIndex = 0;
  for (let i = 1; i < jsonData.length; i++) {
    const row = jsonData[i] as unknown[];
    if (!row || row.length === 0) continue;
    const parsed = parseImportRow(row, headerRow);
    if (!parsed) continue;
    parsedData.push({ ...parsed, key: rowIndex++ });
  }
  return parsedData;
}

export function downloadBoqLineImportTemplate(filename = 'BOQTemplate020.csv'): void {
  const blob = new Blob([BOQ_LINE_IMPORT_TEMPLATE_CSV], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
